import { Controller } from "@hotwired/stimulus";

/**
 * Toast Viewport Component Controller
 * 
 * Manages the toast notification container including:
 * - Enforcing maximum toast limit (FIFO removal)
 * - Toast lifecycle management
 * - Memory-safe cleanup
 */
export default class extends Controller {
  static values = {
    maxToasts: { type: Number, default: 5 }
  };

  declare readonly maxToastsValue: number;

  private observer: MutationObserver | null = null;

  /**
   * Initialize viewport and start observing for new toasts
   */
  connect(): void {
    this.enforceMaxToasts();
    this.observeToastAdditions();
  }

  /**
   * Clean up observer on disconnect
   * CRITICAL: Prevents memory leaks
   */
  disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  /**
   * Enforce maximum toast limit by removing oldest toasts
   */
  private enforceMaxToasts(): void {
    const toasts = this.getToasts();
    const excessCount = toasts.length - this.maxToastsValue;

    if (excessCount > 0) {
      // Remove oldest toasts (FIFO)
      toasts.slice(0, excessCount).forEach((toast) => {
        this.dismissToast(toast);
      });
    }
  }

  /**
   * Observe DOM for new toast additions via Turbo Streams
   */
  private observeToastAdditions(): void {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // Check if any added nodes are toast components
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement && this.isToastElement(node)) {
              this.enforceMaxToasts();
            }
          });
        }
      });
    });

    this.observer.observe(this.element, {
      childList: true,
      subtree: false
    });
  }

  /**
   * Get all toast elements in the viewport
   */
  private getToasts(): HTMLElement[] {
    return Array.from(
      this.element.querySelectorAll('[data-controller*="toast-component"]')
    ) as HTMLElement[];
  }

  /**
   * Check if an element is a toast component
   */
  private isToastElement(element: HTMLElement): boolean {
    return element.hasAttribute("data-controller") &&
           element.getAttribute("data-controller")?.includes("toast-component") === true;
  }

  /**
   * Dismiss a toast by triggering its dismiss action
   */
  private dismissToast(toast: HTMLElement): void {
    // Dispatch a custom event to trigger the toast's dismiss method
    const dismissEvent = new CustomEvent("toast:dismiss", {
      bubbles: true,
      cancelable: true
    });

    // If the toast has a Stimulus controller, call its dismiss method directly
    const controller = this.application.getControllerForElementAndIdentifier(
      toast,
      "application--toolkit--toast-component"
    );

    if (controller && typeof (controller as any).dismiss === "function") {
      (controller as any).dismiss();
    } else {
      // Fallback: remove directly if controller not found
      toast.remove();
    }
  }
}
